import { get, writable } from 'svelte/store';

import { real_id } from 'src/helpers/interface_helpers';
import { deviceRuntimeDiagnostics } from 'src/stores/features/dev/device-runtime-diagnostics';

type StatusUpdatePayload = { id: string; msg: Record<string, unknown> };
type SingleStatPayload = { key: string; value: unknown };
type DeviceRuntimeRecord = Record<string, any>;

function normalizeRuntimeRecord(record: DeviceRuntimeRecord): DeviceRuntimeRecord {
	return Object.entries(record).reduce<DeviceRuntimeRecord>((normalized, [id, value]) => {
		normalized[real_id(id)] = { ...value };
		return normalized;
	}, {});
}

function create_devices_status() {
	const { subscribe, set, update } = writable<DeviceRuntimeRecord>({});

	let pendingFullUpdates: Record<string, Record<string, unknown>> = {};
	let pendingStatUpdates: Record<string, Record<string, unknown>> = {};
	let flushScheduled = false;

	const applyPendingUpdates = (
		status_list: Record<string, Record<string, unknown>>,
	): Record<string, Record<string, unknown>> => {
		const fullUpdates = Object.keys(pendingFullUpdates).length;
		const statDevices = Object.keys(pendingStatUpdates).length;
		const hasFull = fullUpdates > 0;
		const hasStats = statDevices > 0;

		if (!hasFull && !hasStats) {
			return status_list;
		}

		const next = { ...status_list };

		for (const id in pendingFullUpdates) {
			next[id] = pendingFullUpdates[id];
		}

		for (const id in pendingStatUpdates) {
			next[id] = { ...next[id], ...pendingStatUpdates[id] };
		}

		pendingFullUpdates = {};
		pendingStatUpdates = {};
		deviceRuntimeDiagnostics.recordVerbose({
			type: 'status:flush',
			details: {
				fullUpdates,
				statDevices,
			},
		});

		return next;
	};

	const scheduleFlush = () => {
		if (flushScheduled) {
			return;
		}

		flushScheduled = true;
		queueMicrotask(() => {
			flushScheduled = false;
			update((status_list) => applyPendingUpdates(status_list));
		});
	};

	const hydrate = (record: DeviceRuntimeRecord) => {
		const normalized = normalizeRuntimeRecord(record);
		set(normalized);
		deviceRuntimeDiagnostics.record({
			type: 'status:hydrate',
			details: { records: Object.keys(normalized).length },
		});
	};

	return {
		set: hydrate,
		hydrate,
		subscribe,
		update_device_status: (new_status: StatusUpdatePayload) =>
			update((status_list) => {
				return {
					...status_list,
					[real_id(new_status.id)]: { ...new_status.msg },
				};
			}),
		queue_device_status_update: (new_status: StatusUpdatePayload) => {
			pendingFullUpdates[real_id(new_status.id)] = { ...new_status.msg };
			scheduleFlush();
		},
		update_single_stat: (id: string, stat: SingleStatPayload) =>
			update((status_list) => {
				const resolvedId = real_id(id);
				return {
					...status_list,
					[resolvedId]: { ...status_list[resolvedId], [stat.key]: stat.value },
				};
			}),
		queue_single_stat: (id: string, stat: SingleStatPayload) => {
			const resolvedId = real_id(id);
			if (!pendingStatUpdates[resolvedId]) {
				pendingStatUpdates[resolvedId] = {};
			}
			pendingStatUpdates[resolvedId][stat.key] = stat.value;
			scheduleFlush();
		},
		flush_device_status_updates: () => {
			if (
				!flushScheduled &&
				Object.keys(pendingFullUpdates).length === 0 &&
				Object.keys(pendingStatUpdates).length === 0
			) {
				return;
			}
			flushScheduled = false;
			update((status_list) => applyPendingUpdates(status_list));
		},
		remove: (id: string) =>
			update((status_list) => {
				const resolvedId = real_id(id);
				delete pendingFullUpdates[resolvedId];
				delete pendingStatUpdates[resolvedId];
				const { [resolvedId]: _removed, ...remainingStatuses } = status_list;
				deviceRuntimeDiagnostics.record({
					type: 'status:remove',
					details: { id: resolvedId },
				});
				return remainingStatuses;
			}),
		reset: () => {
			pendingFullUpdates = {};
			pendingStatUpdates = {};
			flushScheduled = false;
			set({});
		},
		getByRealId: (id: string | undefined = undefined): any | undefined => {
			const resolvedId = real_id(id);
			const current = get(devices_status)[resolvedId];
			const pendingFull = pendingFullUpdates[resolvedId];
			const pendingStats = pendingStatUpdates[resolvedId];

			if (pendingFull) {
				return pendingStats ? { ...pendingFull, ...pendingStats } : pendingFull;
			}

			if (pendingStats) {
				return current ? { ...current, ...pendingStats } : pendingStats;
			}

			return current;
		},
	};
}

export const devices_status = create_devices_status();

export const getAllDeviceStatus = () => {
	return get(devices_status);
};

function create_devices_settings() {
	const { subscribe, set, update } = writable<DeviceRuntimeRecord>({});
	const hydrate = (record: DeviceRuntimeRecord) => {
		const normalized = normalizeRuntimeRecord(record);
		set(normalized);
		deviceRuntimeDiagnostics.record({
			type: 'settings:hydrate',
			details: { records: Object.keys(normalized).length },
		});
	};

	return {
		set: hydrate,
		hydrate,
		subscribe,
		update_devices_settings: (id: string, new_settings: any) =>
			update((settings_list) => {
				const resolvedId = real_id(id);
				return {
					...settings_list,
					[resolvedId]: { ...new_settings },
				};
			}),
		patch: (
			id: string,
			updater: (settings: Record<string, any> | undefined) => Record<string, any>,
		) =>
			update((settingsList) => {
				const resolvedId = real_id(id);
				const current = settingsList[resolvedId];
				deviceRuntimeDiagnostics.record({
					type: 'settings:patch',
					details: { id: resolvedId },
				});
				return {
					...settingsList,
					[resolvedId]: { ...updater(current ? { ...current } : undefined) },
				};
			}),
		remove: (id: string) =>
			update((settings_list) => {
				const resolvedId = real_id(id);
				const { [resolvedId]: _removed, ...remainingSettings } = settings_list;
				deviceRuntimeDiagnostics.record({
					type: 'settings:remove',
					details: { id: resolvedId },
				});
				return remainingSettings;
			}),
		reset: () => set({}),
		getByRealId: (id: string | undefined = undefined): any | undefined => {
			return get(devices_settings)[real_id(id)];
		},
	};
}

export const devices_settings = create_devices_settings();

export const discovered_statuses = writable({});
export const discovered_settings = writable({});
export const discovered_info = writable({});
